import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Sparkles, Crown, Check, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { lang, t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: m }] = await Promise.all([api.get("/plans"), api.get("/plans/me")]);
        setPlans(p);
        setMe(m);
      } catch {}
    })();
  }, []);

  const subscribe = async (planKey) => {
    if (planKey === "free") {
      toast.info(t("pricing.freeAlready"));
      return;
    }
    if (!auth.getToken()) {
      const email = window.prompt(t("pricing.promptEmailSub"));
      if (!email || !/^\S+@\S+\.\S+$/.test(email.trim())) {
        if (email !== null) toast.error(t("pricing.invalidEmail"));
        return;
      }
      setBusy(planKey);
      try {
        const package_id = planKey === "amateur" ? "sub_premium" : "sub_pro";
        const { data } = await api.post("/stripe/checkout-session", {
          package_id, origin_url: window.location.origin, email: email.trim().toLowerCase(),
        });
        if (data?.url) window.location.href = data.url;
        else throw new Error("No checkout URL");
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("pricing.paymentFailed"));
        setBusy(null);
      }
      return;
    }
    setBusy(planKey);
    try {
      const package_id = planKey === "amateur" ? "sub_premium" : planKey === "pro" ? "sub_pro" : null;
      if (!package_id) throw new Error("Unknown plan");
      const { data } = await api.post("/stripe/checkout-session", {
        package_id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pricing.paymentFailed"));
      setBusy(null);
    }
  };

  const buyPack = async (packageId) => {
    if (!auth.getToken()) {
      const email = window.prompt(t("pricing.promptEmailPack"));
      if (!email || !/^\S+@\S+\.\S+$/.test(email.trim())) {
        if (email !== null) toast.error(t("pricing.invalidEmail"));
        return;
      }
      setBusy(packageId);
      try {
        const { data } = await api.post("/stripe/checkout-session", {
          package_id: packageId, origin_url: window.location.origin, email: email.trim().toLowerCase(),
        });
        if (data?.url) window.location.href = data.url;
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("pricing.paymentFailed"));
        setBusy(null);
      }
      return;
    }
    setBusy(packageId);
    try {
      const { data } = await api.post("/stripe/checkout-session", { package_id: packageId, origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pricing.paymentFailed"));
      setBusy(null);
    }
  };

  const isCurrent = (key) => me?.plan?.key === key;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <div className="text-center max-w-2xl mx-auto">
        <img
          src="/brand/audrey-ai.png"
          alt="geoVINUM AI"
          className="w-20 h-20 mx-auto rounded-full object-cover ring-4 ring-[#FCE4ED] shadow-md mb-4"
          data-testid="pricing-audrey-portrait"
        />
        <span className="chip-pink">{t("pricing.chip")}</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
          {t("pricing.title")}
        </h1>
        <p className="text-[#4A5568] mt-3">
          {t("pricing.subtitle")}
        </p>
        {me?.credits != null && !me.is_guest && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#4A5568] bg-white px-4 py-1.5 rounded-full border border-[#1F3A5F]/12">
            <Coins className="w-4 h-4 text-[#E5276A]" />
            <span className="font-bold text-[#1F3A5F]">{me.credits}</span> {t("pricing.currentCredits")}
          </div>
        )}
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const popular = p.key === "amateur";
          const isPro = p.key === "pro";
          const current = isCurrent(p.key);
          return (
            <div
              key={p.key}
              data-testid={`pricing-card-${p.key}`}
              className={`relative rounded-3xl border-2 p-7 ${
                popular ? "border-[#E5276A] bg-white shadow-xl" :
                isPro ? "border-[#1F3A5F] bg-white" :
                "border-[#1F3A5F]/12 bg-[#FBF7F3]"
              }`}
            >
              {popular && (
                <span className="absolute -top-3 left-7 chip-pink text-[10px] uppercase tracking-wider">
                  {t("pricing.mostPopular")}
                </span>
              )}
              {current && (
                <span className="absolute -top-3 right-7 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-600 text-white text-[10px] font-bold uppercase tracking-wider">
                  <Check className="w-3 h-3" /> {t("pricing.current")}
                </span>
              )}
              {p.key === "pro" && <Crown className="w-6 h-6 text-[#1F3A5F]" />}
              {p.key === "amateur" && <Sparkles className="w-6 h-6 text-[#E5276A]" />}
              <h3 className="font-display text-2xl text-[#1F3A5F] mt-3">{p.name}</h3>
              <p className="text-xs text-[#8B95A1] mt-1">{p[`tagline_${lang}`] || p.tagline_fr}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-[#1F3A5F]">{p.price_eur}€</span>
                <span className="text-sm text-[#8B95A1]">/{t("pricing.perMonth")}</span>
              </div>
              {p.monthly_credits > 0 && (
                <div className="mt-1 text-xs text-[#E5276A] font-bold inline-flex items-center gap-1">
                  <Coins className="w-3 h-3" /> {p.monthly_credits} {t("pricing.creditsIncluded")}
                </div>
              )}
              <ul className="mt-5 space-y-2 text-sm text-[#4A5568]">
                {(p[`features_${lang}`] || p.features).map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-cta-${p.key}`}
                onClick={() => (p.key === "free" ? navigate("/register") : subscribe(p.key))}
                disabled={busy === p.key || current}
                className={`mt-6 w-full py-2.5 rounded-xl font-bold ${
                  current
                    ? "bg-[#FBF7F3] text-[#8B95A1] cursor-default"
                    : popular
                    ? "bg-[#E5276A] text-white hover:bg-[#C01661]"
                    : isPro
                    ? "bg-[#1F3A5F] text-white hover:bg-[#2A4870]"
                    : "bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                } disabled:opacity-60`}
              >
                {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> :
                 current ? t("pricing.currentPlan") :
                 p.key === "free" ? t("pricing.signUp") :
                 t("pricing.subscribe")}
              </button>
            </div>
          );
        })}
      </div>

      {/* Credit packs */}
      <div className="mt-16">
        <h2 className="font-display text-3xl text-[#1F3A5F] text-center">{t("pricing.orBuyPacks")}</h2>
        <p className="text-center text-[#8B95A1] text-sm mt-2">{t("pricing.orBuyPacksHint")}</p>
        <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="credit-packs">
          {[
            { id: "credits_50",  credits: 50,  price: "5 €",   hl: false, save: null },
            { id: "credits_150", credits: 150, price: "12 €",  hl: true,  save: "−20 %" },
            { id: "credits_500", credits: 500, price: "35 €",  hl: false, save: "−30 %" },
          ].map((p) => (
            <div key={p.id} className={`rounded-2xl border p-5 bg-white ${p.hl ? "border-[#E5276A]" : "border-[#1F3A5F]/12"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-1.5"><Coins className="w-5 h-5 text-[#E5276A]" /> {p.credits}</div>
                {p.save && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A]">{p.save}</span>}
              </div>
              <div className="font-display text-3xl text-[#1F3A5F] mt-2">{p.price}</div>
              <button
                data-testid={`pricing-pack-${p.id}`}
                onClick={() => buyPack(p.id)}
                disabled={busy === p.id}
                className={`mt-4 w-full py-2 rounded-xl font-bold text-sm ${p.hl ? "bg-[#E5276A] text-white hover:bg-[#C01661]" : "bg-[#1F3A5F] text-white hover:bg-[#2A4870]"} disabled:opacity-60`}
              >
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("pricing.buy")}
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-xs text-[#8B95A1] mt-10">
        {t("pricing.stripeFooter")}
      </p>
    </div>
  );
}
